const ROUTING = require('./const/routing');
const Url = require('url');
const querystring = require('querystring');

const parallel = function(_tasks, callback) {
	let results = [];
	let tasks = _tasks;
	let isObject = false;

	if (typeof _tasks === 'object' && !Array.isArray(_tasks)) {
		isObject = true;
		tasks = Object.keys(_tasks);
		results = {};
	}
	let count = tasks.length;

	tasks.forEach((_task, index) => {
		let task = _task;
		if (isObject) {
			task = _tasks[_task];
		}
		task((err, data) => {
			if (isObject) {
				results[_task] = data;
			} else {
				results[index] = data;
			}
			if (err) {
				callback && callback(err);
				callback = null;
			}
			if (--count === 0 && callback) {
				callback(null, results);
			}
		});
	});
};

const $ajax = ({transport, transforms=[]}) => {
	return (opts={}) => {
		const {
			type,
			method,
			url,
			data:body,
			dataType,
			// contentType, // Not necessary in json case
			success=()=>{},
			error=()=>{},
		} = opts;

		const json = (dataType === 'json') ? true : false;

		let xhrOpts = {
			method: method || type,
			url,
			json,
			body,
			success,
			error,
		};

		transforms.forEach((fn) => {
			if (typeof fn === 'function') {
				xhrOpts = fn(xhrOpts);
			}
		});

		transport(xhrOpts);
	};
};

const trimSlash = (str) => {
	if (str === '/') {
		return str;
	}
	return str.replace(/\/$/, '');
};

const addTrailingSlash = (str) => {
	return str.replace(/\/?$/, '/');
};

const addLeadingSlash = (str) => {
	if (typeof str === 'string') {
		return (str[0] !== '/') ? `/${str}` : str;
	}
	return str;
};

const parseUrl = (url) => {
	let parsedUrl = null;
	if (typeof url === 'string') {
		parsedUrl = Url.parse(url);
	}
	return parsedUrl;
};

const log = (level, ...args) => {
	const cons = window.console;
	if (cons && cons[level]) {
		cons[level](...args);
	}
};

/**
 * If string, will parse as a URL
 *
 * @param {URL|String} _url
 * @param {Object} opts
 * @returns {String}
 */
const rewriteClientUrl = (_url, opts={}) => {
	const {
		targetHost, // Host to rewrite to
		targetProtocol, // Proto (http|https) to rewrite
		routingMethod, // Check const/routing for possible types
		queryParams, // Addition params to add to URL query string
		makeFullUrl, // Force adding proto/host to whatever the given URL is. Even if just a path.
	} = opts;
	const targetBasePath = trimSlash(opts.targetBasePath || '/');

	let url = _url;
	// This method only works with Url objects
	if (typeof url === 'string') {
		url = Url.parse(_url);
	}
	// If it's a non-http URL like 'mailto:' ignore
	if (url.href && url.href.indexOf('mailto:') === 0) {
		return url.href;
	}
	// Only rewrite host for full URLs. This method supports non-FQDN URLs (path only URLs)
	if (url.host || makeFullUrl) {
		url.protocol = targetProtocol;
		url.host = targetHost;
	}

	// If the given URL has no path at all, do nothing. This should only happen in weird cases with the HTML mutation and <a> used without href or empty href. A code guard.
	if (url.pathname) {
		if (routingMethod === ROUTING.types.path) {
			url.pathname = trimSlash(`${targetBasePath}${url.pathname.replace(targetBasePath, '')}`);

			if (url.query) {
				const parsedQuery = querystring.parse(url.query);
				url.query = {};
				// If we're working with a query-type URL in path mode
				if (parsedQuery[ROUTING.query.key]) {
					const path = decodeURIComponent(parsedQuery[ROUTING.query.key]);
					const parts = path.split('?');
					url.pathname = `${targetBasePath}${parts[0]}`;
					url.path = '';

					// If we had nested query params
					if (parts[1]) {
						const queryParts = querystring.parse(parts[1]);
						for (const key in queryParts) {
							url.query[key] = queryParts[key];
						}
					}
				} else {
					for (let key in parsedQuery) {
						if (parsedQuery[key]) {
							url.query[key] = parsedQuery[key];
						}
					}
				}
			}

			// In "path mode" we simply add additional query params to the end of the URL
			if (Array.isArray(queryParams)) {
				if (!url.query) {
					url.query = {};
				}
				queryParams.forEach((item) => {
					if (item.name && item.value) {
						url.query[item.name] = item.value;
					}
				});
			}
		} else {
			// With the query-based routing there are times webstore JS simply appends query params. I'd rather not require Webstore code be written a specific way and risk breakage in the future. Lets fix by tossing the old routing param
			if (url.query && url.query.indexOf(ROUTING.query.key) === 0) {
				const parsedQuery = querystring.parse(url.query);
				const params = [];
				const oldUrl = Url.parse(decodeURIComponent(parsedQuery[ROUTING.query.key]));
				const oldPath = oldUrl.path || '';

				for (let key in parsedQuery) {
					if (key !== ROUTING.query.key) {
						params.push(`${key}=${parsedQuery[key]}`);
					}
				}
				// Sort for consistent URLs. Looks nicer and prevents random unit test failures due to object ordering/parsing
				params.sort();
				let path = trimSlash(url.pathname.replace(targetBasePath, oldPath));
				if (params.length > 0) {
					path += `?${params.join('&')}`;
				}
				url.query = {
					[ROUTING.query.key]: path,
				};
			} else {
				// Normal query mode. Put the webstore path/info into a query param
				let path = trimSlash(url.pathname);
				if (url.query) {
					path += `?${url.query}`;
				}
				url.query = {
					[ROUTING.query.key]: path,
				};
			}

			// Bolt on extra query params. Used in URL redirect/location cases
			if (Array.isArray(queryParams)) {
				const parts = [];
				queryParams.forEach((item) => {
					if (item.name && item.value) {
						parts.push(`${item.name}=${item.value}`);
					}
				});
				if (parts.length > 0) {
					if (!url.query[ROUTING.query.key] || url.query[ROUTING.query.key].indexOf('?') === -1) {
						// If our magic routing param is empty ('') or does not already have a ? ('/search')
						url.query[ROUTING.query.key] += '?';
					} else {
						// Else we're adding to a query string
						url.query[ROUTING.query.key] += '&';
					}
					url.query[ROUTING.query.key] += parts.join('&');
				}
			}

			url.pathname = targetBasePath;
		}
		url.search = '';
		url.href = '';
	}

	return trimSlash(Url.format(url));
};

module.exports = {
	parallel,
	$ajax,
	log,
	trimSlash,
	parseUrl,
	addTrailingSlash,
	addLeadingSlash,
	rewriteClientUrl,
};
